/**
 * ShowMoreToggle Component
 *
 * Expandable section toggle for advanced fields in settings forms.
 * Shows "Show more options" when collapsed and "Show fewer options" when expanded.
 * During onboarding, this component is not rendered — only essential fields appear.
 */

import React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../utils/cn";

export interface ShowMoreToggleProps {
  /** Whether the advanced section is expanded */
  expanded: boolean;
  /** Callback fired when the toggle is clicked */
  onToggle: () => void;
  /** Advanced field content rendered when expanded */
  children?: React.ReactNode;
}

/**
 * ShowMoreToggle — Expandable advanced fields section
 *
 * Renders a dashed-border toggle button. When expanded, reveals the
 * children content in a region with a dashed top border separator.
 */
export const ShowMoreToggle: React.FC<ShowMoreToggleProps> = ({
  expanded,
  onToggle,
  children,
}) => {
  return (
    <>
      <button
        type="button"
        className="advanced-fields-toggle"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <ChevronDown className={cn("toggle-icon", expanded && "rotate-180")} />
        {expanded ? "Show fewer options" : "Show more options"}
      </button>

      {expanded && (
        <div
          className="advanced-fields-section"
          role="region"
          aria-label="Advanced fields"
        >
          {children}
        </div>
      )}
    </>
  );
};

export default ShowMoreToggle;
